import React, { useEffect, useState } from 'react';
import { Modal, Input, Select, Button, Form } from 'antd';
import FeaturesSelector, { AVAILABLE_FEATURES } from './FeaturesSelector';
import {
  ModalHeader,
  ModalHeaderContent,
  ModalIcon,
  ModalTitle,
  ModalSubtitle,
  ModalBody,
  ModalFooter,
  FormField,
  FormLabel,
  SecondaryButton,
  PrimaryButton,
  FormRow
} from './styles';
import { withTranslation } from 'react-i18next';
import { ADD_ORG_DIALOG } from './translationValues';
import { OrganizationIcon, ShieldIcon } from '../../images/svgIcons';

const { Option } = Select;
const NAMESPACES = [];

const AddOrganizationDialog = ({ t, open, onOpenChange, onAdd, isCreatingOrg, setIsCreatingOrg }) => {
  const initialFormData = {
    organizationName: '',
    type: '',
    phoneNumber: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    configType: 'features',
    features: AVAILABLE_FEATURES
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [features, setFeatures] = useState([]);

  const organizationTypes = [
    'Enterprise',
    'Startup',
    'Small / Medium Business',
    'Non-profit / NGO',
    'Government',
    'Education',
    'Healthcare',
    'Financial Services',
    'Individual / Freelancer',
    'Other'
  ];

  const resetForm = () => {
    setFormData(initialFormData);
    setFeatures(AVAILABLE_FEATURES.map(f => ({ ...f, isEnabled: true })));
    setErrors({});
    setIsCreatingOrg(false);
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setFeatures(AVAILABLE_FEATURES.map(f => ({ ...f, isEnabled: true })));
    setCurrentStep(1);
    onOpenChange(false);
  };

  useEffect(() => {
    if(!open) {
      resetForm();
    } else {
      setFeatures(AVAILABLE_FEATURES.map(f => ({ ...f, isEnabled: true })));
    }
    setCurrentStep(1);
  }, [open])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFeatureUpdate = (features) => {
    setFeatures(features);
  }

  const validateForm = () => {
    const newErrors = {};

    const requiredFields = [
      'organizationName',
      'type',
      'phoneNumber',
      'contactFirstName',
      'contactLastName',
      'contactEmail',
      'addressLine1',
      'city',
      'zip',
      'state',
      'country'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    if (
      formData.contactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    ) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    if (
      formData.phoneNumber &&
      !/^\+?[0-9\s()-]{7,15}$/.test(formData.phoneNumber)
    ) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (
      formData.zip &&
      !/^[a-zA-Z0-9\s-]{3,10}$/.test(formData.zip)
    ) {
      newErrors.zip = 'Please enter a valid ZIP / Postal code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsCreatingOrg(true);
    await onAdd({...formData, features: { configType: 'features', isJsonConfig: true, data: features}});
  };

  const renderError = field =>
    errors[field] && (
      <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
        {errors[field]}
      </div>
    );

  return (
    <Modal
      open={open}
      onCancel={() => onOpenChange(false)}
      footer={null}
      width={currentStep === 1 ? 640 : 900}
      visible={open}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: '16px', overflow: 'hidden' }
      }}
      closeIcon={null}
      maskClosable={false}
    >
      <ModalHeader style={{ marginTop: '10px' }}>
        <ModalHeaderContent>
          <ModalIcon>
            {currentStep === 1 ? <OrganizationIcon /> : <ShieldIcon />}
          </ModalIcon>
          <div>
            <ModalTitle>
              {t(ADD_ORG_DIALOG.MODAL_TITLE.key, ADD_ORG_DIALOG.MODAL_TITLE.value)}
            </ModalTitle>
            <ModalSubtitle>
              {t(
                ADD_ORG_DIALOG.ADD_ORG_SUBTITLE.key,
                ADD_ORG_DIALOG.ADD_ORG_SUBTITLE.value
              )}
            </ModalSubtitle>
          </div>
        </ModalHeaderContent>
      </ModalHeader>

      <ModalBody>
        {currentStep === 1 ? (
          <Form>
        <FormField>
          <FormLabel>{t(ADD_ORG_DIALOG.ORG_NAME_LABEL.key, ADD_ORG_DIALOG.ORG_NAME_LABEL.value)} *</FormLabel>
          <Input
            placeholder="e.g., Tech Innovations Inc."
            value={formData.organizationName}
            onChange={e => handleChange('organizationName', e.target.value)}
            disabled={isCreatingOrg}
            style={{
              height: '48px',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          />
          {renderError('organizationName')}
        </FormField>

        <FormField>
          <FormLabel>{t(ADD_ORG_DIALOG.ORG_TYPE_LABEL.key, ADD_ORG_DIALOG.ORG_TYPE_LABEL.value)} *</FormLabel>
          <Select
            className='organization-type'
            placeholder='Select organization type'
            value={formData.type || undefined}
            onChange={value => handleChange('type', value)}
            disabled={isCreatingOrg}
            style={{ width: '100%', borderRadius: '12px !important' }}
            size="large"
            getPopupContainer={triggerNode => triggerNode.parentNode}
          >
            {organizationTypes.map(type => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
          {renderError('type')}
        </FormField>

        <FormField>
          <FormLabel>{t(ADD_ORG_DIALOG.PHONE_NUMBER_LABEL.key, ADD_ORG_DIALOG.PHONE_NUMBER_LABEL.value)} *</FormLabel>
          <Input
            placeholder="Enter phone number"
            value={formData.phoneNumber}
            onChange={e => handleChange('phoneNumber', e.target.value)}
            disabled={isCreatingOrg}
            style={{
              height: '48px',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          />
          {renderError('phoneNumber')}
        </FormField>

        <FormRow>
          <FormField>
            <FormLabel>{t(ADD_ORG_DIALOG.FIRSTNAME_LABEL.key, ADD_ORG_DIALOG.FIRSTNAME_LABEL.value)} *</FormLabel>
            <Input
              placeholder="First name"
              value={formData.contactFirstName}
              onChange={e =>
                handleChange('contactFirstName', e.target.value)
              }
              disabled={isCreatingOrg}
              style={{
                height: '48px',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white'
              }}
            />
            {renderError('contactFirstName')}
          </FormField>

          <FormField>
            <FormLabel>{t(ADD_ORG_DIALOG.LASTNAME_LABEL.key, ADD_ORG_DIALOG.LASTNAME_LABEL.value)} *</FormLabel>
            <Input
              placeholder="Last name"
              value={formData.contactLastName}
              onChange={e =>
                handleChange('contactLastName', e.target.value)
              }
              disabled={isCreatingOrg}
              style={{
                height: '48px',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white'
              }}
            />
            {renderError('contactLastName')}
          </FormField>
        </FormRow>

        <FormField>
          <FormLabel>{t(ADD_ORG_DIALOG.EMAIL_LABEL.key, ADD_ORG_DIALOG.EMAIL_LABEL.value)} *</FormLabel>
          <Input
            type="email"
            placeholder="email@example.com"
            value={formData.contactEmail}
            onChange={e => handleChange('contactEmail', e.target.value)}
            disabled={isCreatingOrg}
            style={{
              height: '48px',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          />
          {renderError('contactEmail')}
        </FormField>

        <FormField>
          <FormLabel>{t(ADD_ORG_DIALOG.ADDRESS_LINE1_LABEL.key, ADD_ORG_DIALOG.ADDRESS_LINE1_LABEL.value)} *</FormLabel>
          <Input
            placeholder="Street address"
            value={formData.addressLine1}
            onChange={e => handleChange('addressLine1', e.target.value)}
            disabled={isCreatingOrg}
            style={{
              height: '48px',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          />
          {renderError('addressLine1')}
        </FormField>

        <FormField>
          <FormLabel>{t(ADD_ORG_DIALOG.ADDRESS_LINE2_LABEL.key, ADD_ORG_DIALOG.ADDRESS_LINE2_LABEL.value)}</FormLabel>
          <Input
            placeholder="Apartment, suite, etc. (optional)"
            value={formData.addressLine2}
            onChange={e => handleChange('addressLine2', e.target.value)}
            disabled={isCreatingOrg}
            style={{
              height: '48px',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          />
          {renderError('addressLine2')}
        </FormField>

        <FormRow>
          <FormField>
            <FormLabel>{t(ADD_ORG_DIALOG.CITY_LABEL.key, ADD_ORG_DIALOG.CITY_LABEL.value)} *</FormLabel>
            <Input
              placeholder="City"
              value={formData.city}
              onChange={e => handleChange('city', e.target.value)}
              disabled={isCreatingOrg}
              style={{
                height: '48px',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white'
              }}
            />
            {renderError('city')}
          </FormField>

          <FormField>
            <FormLabel>{t(ADD_ORG_DIALOG.ZIPCODE_LABEL.key, ADD_ORG_DIALOG.ZIPCODE_LABEL.value)} *</FormLabel>
            <Input
              placeholder="ZIP"
              value={formData.zip}
              onChange={e => handleChange('zip', e.target.value)}
              disabled={isCreatingOrg}
              style={{
                height: '48px',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white'
              }}
            />
            {renderError('zip')}
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <FormLabel>{t(ADD_ORG_DIALOG.STATE_LABEL.key, ADD_ORG_DIALOG.STATE_LABEL.value)} *</FormLabel>
            <Input
              placeholder="State"
              value={formData.state}
              onChange={e => handleChange('state', e.target.value)}
              disabled={isCreatingOrg}
              style={{ height: '48px', width: '100%', borderRadius: '4px', }}
              size="large"
            />
            {renderError('state')}
          </FormField>

          <FormField>
            <FormLabel>{t(ADD_ORG_DIALOG.COUNTRY_LABEL.key, ADD_ORG_DIALOG.COUNTRY_LABEL.value)} *</FormLabel>
            <Input
              placeholder="Country"
              value={formData.country}
              onChange={e => handleChange('country', e.target.value)}
              disabled={isCreatingOrg}
              style={{ height: '48px', width: '100%', borderRadius: '4px', }}
              size="large"
            />
            {renderError('country')}
          </FormField>
        </FormRow>
        </Form>) : (
          <div>
            <FeaturesSelector
              features={features}
              onChange={handleFeatureUpdate}
            />
          </div>
        )}
      </ModalBody>

        <ModalFooter>
          {currentStep === 1 ? (
            <>
              <SecondaryButton onClick={handleCancel}>
                {t(ADD_ORG_DIALOG.CANCEL_LABEL.key, ADD_ORG_DIALOG.CANCEL_LABEL.value)}
              </SecondaryButton>
              <PrimaryButton onClick={handleNext} style={{ flex: 1 }}>
                Next
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton onClick={handleBack}>
                Back
              </SecondaryButton>
                {!isCreatingOrg ? (
                <PrimaryButton onClick={handleSubmit} style={{ flex: 1 }}>
                  {t(ADD_ORG_DIALOG.ADD_ORG_TITLE.key, ADD_ORG_DIALOG.ADD_ORG_TITLE.value)}
                </PrimaryButton>
              ) : (
                <Button loading={isCreatingOrg} disabled style={{padding: '14px 24px',
                  backgroundColor: '#f9df25',
                  color: 'black',
                  borderRadius: '4px',
                  transition: 'all 0.3s',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  height: '43px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem'}}>{t(ADD_ORG_DIALOG.ADDING_ORG_TITLE.key, ADD_ORG_DIALOG.ADDING_ORG_TITLE.value)}
                </Button>
              )}
            </>
          )}
        </ModalFooter>
    </Modal>
  );
};

export default withTranslation(NAMESPACES)(AddOrganizationDialog);
